import { useEffect, useRef, useState } from 'react';

const INDIA_TIMEZONE = 'Asia/Kolkata';

type DayFirstInputKind = 'date' | 'datetime-local';

export function OfferCampaignDateInput({
  value,
  kind,
  disabled = false,
  onChange,
}: {
  value: string;
  kind: DayFirstInputKind;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const formattedValue = kind === 'date'
    ? formatDateInputValue(value)
    : formatDateTimeInputValue(value);
  const [displayValue, setDisplayValue] = useState(formattedValue);
  const [editing, setEditing] = useState(false);
  const pickerRef = useRef<HTMLInputElement>(null);
  const pickerValue = valueForNativePicker(value, kind);

  useEffect(() => {
    if (!editing) setDisplayValue(formattedValue);
  }, [editing, formattedValue]);

  const update = (next: string) => {
    setDisplayValue(next);
    if (!next.trim()) {
      onChange('');
      return;
    }
    const canonical = kind === 'date'
      ? parseDayFirstDateInput(next)
      : parseDayFirstDateTimeInput(next);
    // Clear the stored value while input is incomplete or invalid. This prevents
    // Save from silently retaining the previously displayed date.
    onChange(canonical || '');
  };

  const finishEditing = () => {
    setEditing(false);
    const canonical = kind === 'date'
      ? parseDayFirstDateInput(displayValue)
      : parseDayFirstDateTimeInput(displayValue);
    if (!canonical) setDisplayValue('');
  };

  const openPicker = () => {
    const picker = pickerRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (!picker || disabled) return;
    if (typeof picker.showPicker === 'function') picker.showPicker();
    else {
      picker.focus();
      picker.click();
    }
  };

  return (
    <div className="offer-date-picker">
      <input
        className="offer-date-picker-display"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={kind === 'date' ? 'DD/MM/YYYY' : 'DD/MM/YYYY hh:mm AM/PM'}
        value={displayValue}
        disabled={disabled}
        onFocus={() => setEditing(true)}
        onBlur={finishEditing}
        onChange={(event) => update(event.target.value)}
      />
      <button
        className="offer-date-picker-button"
        type="button"
        disabled={disabled}
        aria-label={kind === 'date' ? 'Open date calendar' : 'Open date and time calendar'}
        title={kind === 'date' ? 'Select date' : 'Select date and time'}
        onClick={openPicker}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M7 2v3M17 2v3M3 9h18" />
          <rect x="3" y="4" width="18" height="17" rx="2" />
          <path d="M7 13h3M14 13h3M7 17h3M14 17h3" />
        </svg>
      </button>
      <input
        ref={pickerRef}
        className="offer-date-picker-native"
        type={kind}
        value={pickerValue}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const next = event.target.value;
          onChange(next);
          setDisplayValue(kind === 'date' ? formatDateInputValue(next) : formatDateTimeInputValue(next));
        }}
      />
    </div>
  );
}

export function formatOfferCampaignDate(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const direct = parseDateParts(raw);
  if (direct) return `${two(direct.day)}/${two(direct.month)}/${direct.year}`;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const parts = datePartsInIndia(date);
  return `${parts.day}/${parts.month}/${parts.year}`;
}

export function formatOfferCampaignDateTime(value: unknown, includeIst = false): string {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const date = parseIndiaDateTime(raw);
  if (!date) return raw;
  const parts = dateTimePartsInIndia(date);
  const output = `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute} ${parts.dayPeriod}`;
  return includeIst ? `${output} IST` : output;
}

export function formatDateInputValue(value: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const parts = parseDateParts(raw);
  return parts ? `${two(parts.day)}/${two(parts.month)}/${parts.year}` : '';
}

export function formatDateTimeInputValue(value: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const local = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (local) {
    const [, year, month, day, hour, minute] = local;
    if (!validDate(Number(year), Number(month), Number(day)) || Number(hour) > 23 || Number(minute) > 59) return '';
    return `${day}/${month}/${year} ${toTwelveHour(Number(hour), minute)}`;
  }
  const date = parseIndiaDateTime(raw);
  if (!date) return '';
  const parts = dateTimePartsInIndia(date);
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute} ${parts.dayPeriod}`;
}

function parseIndiaDateTime(value: string): Date | null {
  const dayFirst = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (dayFirst) {
    const [, day, month, year, hourRaw, minuteRaw, periodRaw] = dayFirst;
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    if (!validDate(Number(year), Number(month), Number(day)) || hour < 1 || hour > 12 || minute > 59) return null;
    const hour24 = periodRaw.toUpperCase() === 'AM' ? hour % 12 : (hour % 12) + 12;
    return new Date(`${year}-${two(Number(month))}-${two(Number(day))}T${two(hour24)}:${two(minute)}:00+05:30`);
  }

  // A timezone-free value from a datetime-local field represents IST by
  // contract. Never let the browser reinterpret it in the device timezone.
  const localIso = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (localIso) {
    const [, year, month, day, hour, minute, second = '00'] = localIso;
    if (!validDate(Number(year), Number(month), Number(day)) || Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59) return null;
    return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}+05:30`);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function parseDayFirstDateInput(value: string): string | null {
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const [, day, month, year] = match;
  if (!validDate(Number(year), Number(month), Number(day))) return null;
  return `${year}-${two(Number(month))}-${two(Number(day))}`;
}

export function parseDayFirstDateTimeInput(value: string): string | null {
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  const [, day, month, year, hourRaw, minuteRaw, periodRaw] = match;
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!validDate(Number(year), Number(month), Number(day)) || hour < 1 || hour > 12 || minute > 59) return null;
  const period = periodRaw.toUpperCase();
  const hour24 = period === 'AM' ? hour % 12 : (hour % 12) + 12;
  return `${year}-${two(Number(month))}-${two(Number(day))}T${two(hour24)}:${two(minute)}`;
}

function valueForNativePicker(value: string, kind: DayFirstInputKind): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (kind === 'date') {
    const parts = parseDateParts(raw);
    return parts ? `${parts.year}-${two(parts.month)}-${two(parts.day)}` : '';
  }
  const local = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (local) return `${local[1]}-${local[2]}-${local[3]}T${local[4]}:${local[5]}`;
  const date = parseIndiaDateTime(raw);
  if (!date) return '';
  const parts = dateTimePartsInIndia(date);
  const hour12 = Number(parts.hour);
  const hour24 = parts.dayPeriod === 'PM' ? (hour12 % 12) + 12 : hour12 % 12;
  return `${parts.year}-${parts.month}-${parts.day}T${two(hour24)}:${parts.minute}`;
}

function parseDateParts(value: string): { year: number; month: number; day: number } | null {
  const dmy = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s|$)/);
  if (dmy) {
    const [, day, month, year] = dmy;
    return validDate(Number(year), Number(month), Number(day))
      ? { year: Number(year), month: Number(month), day: Number(day) }
      : null;
  }
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!iso) return null;
  const [, year, month, day] = iso;
  return validDate(Number(year), Number(month), Number(day))
    ? { year: Number(year), month: Number(month), day: Number(day) }
    : null;
}

function validDate(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

function datePartsInIndia(date: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: INDIA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  return partMap(parts);
}

function dateTimePartsInIndia(date: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: INDIA_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const mapped = partMap(parts);
  return { ...mapped, dayPeriod: mapped.dayPeriod.toUpperCase() };
}

function partMap(parts: Intl.DateTimeFormatPart[]) {
  return parts.reduce<Record<string, string>>((result, part) => {
    if (part.type !== 'literal') result[part.type] = part.value;
    return result;
  }, {}) as Record<'year' | 'month' | 'day' | 'hour' | 'minute' | 'dayPeriod', string>;
}

function toTwelveHour(hour24: number, minute: string): string {
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour = hour24 % 12 || 12;
  return `${two(hour)}:${minute} ${period}`;
}

function two(value: number): string {
  return String(value).padStart(2, '0');
}
